/**
 * Data shapes the simulation consumes. `packages/content` validates JSON against zod schemas whose
 * output is structurally compatible with these.
 *
 * Frame conventions: world ground plane is (x, z), with y up. A car with yaw ψ faces
 * (sin ψ, cos ψ), so ψ = 0 faces +z and matches Three's `rotation.y`. Body axes are x forward,
 * y left, and yaw is counter-clockwise seen from above. Steering, lateral velocity, slip and
 * yaw rate are all left-positive; `InputFrame.steer` is right-positive (the player's view).
 */

export type Drivetrain = "RWD" | "AWD";
export type SurfaceType = "dry" | "paint" | "wet";

export interface TorquePoint {
  rpm: number;
  /** Engine torque in N·m. */
  torque: number;
}

export interface AxleGrip {
  /** Peak friction coefficient (lateral grip per unit load). */
  mu: number;
  /** Slip angle at peak grip, degrees. */
  peakSlipDeg: number;
  /** Fraction of peak grip left once fully past the peak (0..1). */
  falloff: number;
  /** How far past the peak (in multiples of the peak slip angle) the falloff completes. */
  falloffSpan: number;
}

export interface CarParams {
  /** kg */
  mass: number;
  /** Centre-of-gravity height, m. */
  cogHeight: number;
  wheelbase: number;
  /** Static fraction of weight on the front axle. */
  frontWeight: number;
  /** Yaw inertia = mass × factor × wheelbase². */
  yawInertiaFactor: number;
  /** Extra yaw-rate damping, 1/s. */
  yawDamping: number;
  /** Body size for wall collision, m. */
  length: number;
  width: number;
  /**
   * How much of an axle's lateral grip longitudinal force uses up (0 = none, 1 = a full friction
   * circle). Below 1, power and braking cost less sideways grip, which keeps slides catchable.
   */
  combinedSlip: number;
  /** Multiplier on physical load transfer, so weight shifts read clearly. */
  weightTransferGain: number;

  wheelRadius: number;
  drivetrain: Drivetrain;
  /** AWD only: fraction of drive torque sent to the front axle. */
  awdFrontSplit: number;
  torqueCurve: TorquePoint[];
  idleRpm: number;
  redlineRpm: number;
  /** Clutch-slip floor for rpm in first gear under throttle. */
  launchRpm: number;
  gearRatios: number[];
  finalDrive: number;
  drivetrainEfficiency: number;
  upshiftRpm: number;
  downshiftRpm: number;
  /** Ticks of zero torque during a gear change. */
  shiftTicks: number;

  topSpeedKmh: number;
  /** Aerodynamic drag, N per (m/s)². */
  dragCoeff: number;
  /** Rolling resistance, N per (m/s). */
  rollingResistance: number;
  /** Max braking deceleration, m/s². */
  brakeDecel: number;
  /** Fraction of braking on the front axle. */
  brakeBias: number;
  /** Engine braking at redline, m/s². Scales with rpm and fades with throttle. */
  engineBraking: number;
  /** Deceleration applied while rolling or sliding backwards without reverse engaged, m/s². */
  backwardDecel: number;
  /** Reverse: hold the brake with no throttle once (nearly) stopped and the car backs up. */
  reverse: {
    /** Acceleration at full brake, m/s². */
    accel: number;
    topSpeedKmh: number;
    /** Forward speed below which the brake becomes reverse, m/s. */
    engageSpeed: number;
  };

  gripFront: AxleGrip;
  gripRear: AxleGrip;
  handbrake: {
    /** Multiplier on rear grip while the handbrake is fully on. */
    rearGrip: number;
    /** Rear braking deceleration, m/s². */
    brakeDecel: number;
    /** Per-tick smoothing towards on / off (0..1). */
    attack: number;
    release: number;
  };
  steering: {
    lockDeg: number;
    /** Fraction of lock left at top speed. */
    highSpeedFactor: number;
    /** Body slip angle at which full lock is available again, degrees. */
    slideOpenDeg: number;
    /** Keyboard steering ramp, full-lock units per second (client use). */
    speed: number;
  };
  assists: {
    /** Max blend towards the front axle's velocity direction (0..1). */
    counterSteer: number;
    counterSteerStartDeg: number;
    counterSteerRangeDeg: number;
    /** Body slip angle beyond which rear grip is boosted to stop spins. */
    angleCapDeg: number;
    angleCapGain: number;
    /** Throttle slew at full smoothing, units per second. */
    throttleSlew: number;
  };
}

/** Runtime assist scaling (settings toggle). 0 = off, 1 = the car's own strength. */
export interface AssistSettings {
  counterSteer: number;
  angleCap: number;
  throttleSmoothing: number;
}

export const ASSISTS_ON: Readonly<AssistSettings> = {
  counterSteer: 1,
  angleCap: 1,
  throttleSmoothing: 1,
};
export const ASSISTS_OFF: Readonly<AssistSettings> = {
  counterSteer: 0,
  angleCap: 0,
  throttleSmoothing: 0,
};

export interface WallTuning {
  /** Wall-normal impact speed at or above which the hit is a crash, m/s. */
  crashNormalSpeed: number;
  /** Speed lost per second while scraping, m/s². */
  scrapeDecel: number;
  /** Fraction of the impact speed also lost from the along-wall speed. */
  scrapeLoss: number;
  /** Bounce off the wall (0..1). */
  restitution: number;
  /** Yaw-rate fraction removed per scraping tick. */
  yawDamping: number;
  /** Ticks the car is held still after a crash reset. */
  crashLockTicks: number;
}

export interface SimTuning {
  /** Distance between resampled track points, m. */
  sampleSpacing: number;
  /** Nearest-point search radius in samples around the previous result. */
  searchWindow: number;
  /** Speed floor for slip-angle maths, m/s. */
  lowSpeed: number;
  /** Grip multiplier per surface type. */
  surfaces: Record<SurfaceType, number>;
  wall: WallTuning;
}

export interface TrackControlPoint {
  x: number;
  y: number;
  z: number;
  /** Wall-to-wall width, m. */
  width: number;
  bank: number;
}

export interface TrackData {
  id: string;
  controlPoints: TrackControlPoint[];
  /** Surface from control point `fromPoint` onwards, until the next entry. */
  surfaces: { fromPoint: number; surface: SurfaceType }[];
  /** Grid position, m along the track. */
  startDistance: number;
  /** Finish line, m before the end of the spline (the road runs on past it). */
  finishFromEnd: number;
}
